// Timeline preview playback — Phase 5 Task 4.
//
// Wires the Timeline's play button to a real Web Audio preview:
//   - Decodes every favorite Take's ArrayBuffer to an AudioBuffer on demand
//     through the shared LRU cache in lib/audioPlayback.ts (keyed by take.id,
//     bounded, dropped on invalidate). Nothing is cached ON the Take object
//     any more — a decoded AudioBuffer on the Take made IndexedDB `put()`
//     throw DataCloneError and let memory grow unbounded (Audit M8c/L2).
//   - Schedules one AudioBufferSourceNode per favorite, at its block offset,
//     routed through the mixer's voice-gain node (so mute + Safari
//     MediaStream tap work).
//   - Syncs the <video> element to the playhead: seek to startFrom, then
//     play() after the measured output-latency so audio + video meet at the
//     same perceptual moment.
//   - rAF loop exposes `playheadSec` (React state) read from the
//     AudioContext clock, compensated for device latency.
//   - Auto-ducks the <video> while a voice block is audible; unducks
//     in the gap between blocks + at preview-end.
//   - Invalidate-aware: a pre-play context-state check (not 'running') closes
//     and recreates the context; audioPlayback drops its decode cache on the
//     same invalidate channel so the preview recovers from Safari's
//     silent-death-under-memory-pressure state.
//
// Legacy reference: legacy/index.html lines 7897–8210 (the core preview
// pipeline: _schedulePreviewFrom + playTimelinePreview + updatePlayhead +
// finishPreview). docs/patterns-timeline.md summarises the Safari-specific
// workarounds.
//
// Hook is owned by Timeline.tsx — there's exactly one Timeline mounted per
// view, so a single active preview at a time is the intended model.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Block, Take } from '@shared/types';
import * as AudioCtx from '@/lib/audioContext';
import {
  ensureVoiceGain,
  getAudioContext,
  invalidatePreviewAudio,
  isSafariMediaStreamPath,
  measureOutputLatency,
  onPreviewAudioInvalidate,
  setPreviewOutputLatency,
  startSafariAudioTap,
  stopAllPreviewSources,
  trackPreviewSource,
  warmupSilentBuffer,
} from '@/lib/audioContext';
import { getActiveVideo, seekVideoTo } from '@/lib/activeVideo';
import {
  useMixerStore,
  effectiveVoiceVolume,
} from '@/stores/mixer';
import { useSessionStore } from '@/stores/session';
import { decodeAndCache } from '@/lib/audioPlayback';
import { useVideoDucking } from '@/hooks/useVideoDucking';
import { resolveBedPlacement } from '@/lib/bedPlacement';
import {
  decodeBedBuffer,
  getCachedBedBuffer,
  invalidateBedRouting,
  scheduleBed,
  stopBedSource,
} from '@/lib/sfxBedRouting';
import {
  onStopPreview,
  requestCancelPipeline,
} from '@/lib/pipelineCoordinator';
import {
  clearPlayback,
  registerPlaybackStop,
  startPlayback,
} from '@/stores/playback';
import { activeBlockAt, duckTransition, previewElapsed, voiceStartParams } from '@/lib/previewSchedule';

interface ScheduleEntry {
  take: Take;
  offset: number;     // seconds on the timeline
  blockIdx: number;
  duration: number;   // take.duration (seconds)
  /** Decoded audio for this play — filled by `play()` from the shared
   *  decode cache, never stored on the Take. */
  buffer?: AudioBuffer;
}

// ── Music/SFX bed routing ────────────────────────────────────────────────
//
// The bed is a single per-asset audio file that plays alongside the voice
// during preview. State lives in src/lib/sfxBedRouting.ts as a module-
// level singleton so STSMasterPreview can share the same bedGain (and
// inherit the same mixer-slider/duck wiring). useVideoDucking pushes
// slider changes via sfxBedRouting.applyBedGainExternal directly.

interface PreviewInternals {
  /** True between play() and stop()/finish(). */
  playing: boolean;
  /** ac.currentTime at schedule time — getElapsed() subtracts this. */
  baseContextTime: number;
  /** Timeline seconds corresponding to baseContextTime (i.e. `from` arg). */
  baseElapsed: number;
  /** Schedule entries for the current play. */
  schedule: ScheduleEntry[];
  /** Total timeline duration (used by the rAF loop to detect end-of-preview). */
  totalDuration: number;
  /** Display asset length (getAssetTimelineDuration boundary) — the length beds resolve against. NOT the voice-widened totalDuration. */
  bedAssetLen: number;
  /** Cancellation flag — rAF loop checks each frame. */
  cancel: boolean;
  /** Last-rendered active block idx (for duck/unduck edge detection). */
  lastActiveBlock: number;
  /** rAF handle — nullable so stop() can cancel it. */
  rafHandle: number | null;
  /** Pending setTimeout handles (e.g. delayed video.play() after latency).
   *  stop() must clear these so the deferred call doesn't fire after the
   *  user has already aborted — otherwise the <video> resumes silently
   *  post-stop and emits a brief audio tail. */
  pendingTimeouts: number[];
}

function newInternals(): PreviewInternals {
  return {
    playing: false,
    baseContextTime: 0,
    baseElapsed: 0,
    schedule: [],
    totalDuration: 0,
    bedAssetLen: 0,
    cancel: false,
    lastActiveBlock: -1,
    rafHandle: null,
    pendingTimeouts: [],
  };
}

/** Returns true if the given context is in a non-running state that needs
 *  a fresh rebuild before scheduling. Safari under memory pressure can flip
 *  a context to 'interrupted' or leave it in 'suspended' — invalidate +
 *  recreate on the next preview is the recovery path. */
function contextNeedsRebuild(ac: AudioContext): boolean {
  return ac.state === 'closed' || ac.state === 'interrupted';
}

export function useTimelinePreview(blocks: Block[], totalDuration: number, bedAssetLen: number) {
  const ref = useRef<PreviewInternals>(newInternals());
  const [isPlaying, setIsPlayingState] = useState<boolean>(false);
  const [playheadSec, setPlayheadSec] = useState<number>(0);

  const { duck, unduck } = useVideoDucking();

  // Bed routing is bound to the AudioContext — drop it when the context is
  // torn down so the next play() rebuilds + re-decodes. (Take decodes live in
  // audioPlayback's cache, which clears itself on the same channel.)
  useEffect(() => {
    const off = onPreviewAudioInvalidate(() => {
      invalidateBedRouting();
    });
    return off;
  }, []);

  // --- Internal: schedule sources starting at `startFrom` ------------------
  const scheduleFrom = useCallback((startFrom: number) => {
    const r = ref.current;
    const ac = getAudioContext();

    const voiceGain = ensureVoiceGain(
      effectiveVoiceVolume(useMixerStore.getState()),
    );
    // Ensure any stale sources are gone before we queue fresh ones.
    stopAllPreviewSources();

    r.baseContextTime = ac.currentTime;
    r.baseElapsed = startFrom;

    let latency = measureOutputLatency(ac);
    // Safari MediaStream path bypasses ac.destination.outputLatency. Its
    // internal MediaStream buffer is small (~20ms); subtracting the inflated
    // Safari fallback (0.35s) would make the visual playhead lag the audio.
    if (isSafariMediaStreamPath()) latency = 0;
    setPreviewOutputLatency(latency);

    // Anchor every src.start() at the SAME context-time snapshot
    // (r.baseContextTime, captured above) instead of re-reading
    // ac.currentTime on each iteration. Web Audio treats a slightly-past
    // `when` as "start immediately", so the shared anchor is always safe.
    const anchor = r.baseContextTime;

    // Music/SFX beds FIRST. Scheduling them before the voice loop keeps the
    // JS gap between bed.start() and the first voice.start() as small as
    // possible — the voice loop's per-iteration work was pushing the bed's
    // effective start ~half a second behind the voice on heavy timelines.
    // Voice clips are still anchored to the same `anchor`, so the relative
    // alignment is preserved. Both bed tracks (max 2) are scheduled here.
    stopBedSource();
    const session = useSessionStore.getState();
    const activeBeds = session.sfxBeds[session.currentAssetIdx] ?? [];
    activeBeds.forEach((bed, idx) => {
      const trackIdx = (idx === 0 ? 0 : 1) as 0 | 1;
      const cached = getCachedBedBuffer(ac, bed.buffer, trackIdx);
      if (cached) {
        const placement = resolveBedPlacement(bed, r.bedAssetLen);
        scheduleBed(ac, cached, placement, startFrom, anchor, trackIdx);
      }
    });

    for (const entry of r.schedule) {
      const buffer = entry.buffer;
      if (!buffer) continue;
      const start = voiceStartParams(entry.offset, buffer.duration, startFrom, anchor);
      if (!start) continue;

      const src = ac.createBufferSource();
      src.buffer = buffer;
      // Safety: connect to voiceGain only if its context matches — mismatch
      // indicates a stale gain node leftover from a closed context.
      if (voiceGain.context === ac) {
        src.connect(voiceGain);
      } else {
        src.connect(ac.destination);
      }
      trackPreviewSource(src);

      src.start(start.when, start.offsetIntoBuffer);
    }

    // Safari path — re-bind the MediaStream to the bridging <audio>. Must
    // happen on every schedule because stop() clears srcObject to prevent
    // last-frame stutter.
    startSafariAudioTap();
  }, []);

  // --- rAF loop ------------------------------------------------------------
  const runRaf = useCallback(() => {
    const r = ref.current;
    const ac = getAudioContext();
    const tick = () => {
      if (r.cancel) {
        finish();
        return;
      }
      const latency = isSafariMediaStreamPath() ? 0 : measureOutputLatency(ac);
      const { raw, elapsed } = previewElapsed(r.baseElapsed, r.baseContextTime, ac.currentTime, latency);
      setPlayheadSec(elapsed);

      // Active-block detection — we use `raw` (pre-clamp) so a block at
      // offset 0 doesn't duck during the pre-roll before audio actually
      // emits. Match legacy logic at line 8150.
      const activeBlock = activeBlockAt(r.schedule, raw);
      if (activeBlock !== r.lastActiveBlock) {
        // duck() already pushes the ramp through to every bed track via
        // applyBedGainExternalAll — calling applyBedGain() instant here
        // would cancel the attack ramp. Same for unduck() below.
        const t = duckTransition(r.lastActiveBlock, activeBlock);
        if (t === 'duck') duck();
        else if (t === 'unduck') unduck();
        r.lastActiveBlock = activeBlock;
      }

      // Natural end-of-preview: reached the end of the timeline.
      if (r.totalDuration > 0 && elapsed >= r.totalDuration) {
        setPlayheadSec(0);
        finish();
        return;
      }

      r.rafHandle = requestAnimationFrame(tick);
    };

    const finish = () => {
      stopAllPreviewSources();
      stopBedSource();
      // Clear any deferred video.play() timeouts from the latency alignment
      // window — same rationale as stop().
      for (const h of r.pendingTimeouts) {
        try { window.clearTimeout(h); } catch { /* ignore */ }
      }
      r.pendingTimeouts = [];
      if (r.lastActiveBlock !== -1) {
        unduck();
        r.lastActiveBlock = -1;
      }
      r.playing = false;
      r.cancel = false;
      r.rafHandle = null;
      setIsPlayingState(false);
      // Pause the video — matches legacy finishPreview() at line 8202.
      const v = getActiveVideo();
      if (v && !v.paused) {
        try { v.pause(); } catch { /* ignore */ }
      }
      // Release the global playback mutex slot if it's still ours.
      clearPlayback('timeline');
    };

    r.rafHandle = requestAnimationFrame(tick);
  }, [duck, unduck]);

  // --- play ---------------------------------------------------------------
  const play = useCallback(
    async (startFrom = 0) => {
      const r = ref.current;
      if (r.playing) return;

      // Cancel any running Generate-All / Play-All before we take over the
      // shared voice-gain bus.
      requestCancelPipeline();

      // Claim the global playback mutex — this fires the take-playback
      // stop handler (registered by TTSView) so a chip / autoplay take
      // goes silent before our Web Audio sources start. The pipeline stop
      // is also covered by this mutex, but we keep the explicit
      // requestCancelPipeline() above for the legacy listener path.
      startPlayback('timeline');

      // Build the schedule from the current blocks snapshot. Only blocks
      // that have a favorite take (with a non-zero duration) participate.
      const entries: ScheduleEntry[] = [];
      blocks.forEach((b, i) => {
        if (b.favIdx < 0) return;
        const take = b.takes[b.favIdx];
        if (!take || !take.buffer) return;
        entries.push({
          take,
          offset: b.offset || 0,
          blockIdx: i,
          duration: take.duration || 0,
        });
      });
      entries.sort((a, b) => a.offset - b.offset);
      // No early-return if entries is empty — we still want to start the
      // <video> element + rAF loop so the user can preview the video's own
      // audio + see the playhead move even before any voice is recorded.

      r.schedule = entries;
      r.totalDuration = totalDuration;
      r.bedAssetLen = bedAssetLen;
      r.cancel = false;
      r.lastActiveBlock = -1;

      // Get/resume the context. If it's in a non-running state, rebuild
      // from scratch so we recover from Safari's memory-pressure death.
      let ac = getAudioContext();
      if (ac.state === 'suspended') {
        try { await ac.resume(); } catch { /* ignore */ }
      }
      if (contextNeedsRebuild(ac)) {
        invalidatePreviewAudio('context not running on play');
        ac = getAudioContext();
        if (ac.state === 'suspended') {
          try { await ac.resume(); } catch { /* ignore */ }
        }
      }

      // Cold-start backup warmup. Eager-init runs on first pointerdown;
      // this covers the edge case where a preview starts before any gesture
      // bubbled (or the context was just recreated above).
      if (ac.currentTime < 0.05) {
        try {
          await warmupSilentBuffer();
        } catch { /* ignore */ }
      }

      // Decode all takes in parallel through the shared LRU cache (keyed by
      // take.id — a second play is instant). allSettled so one failure
      // doesn't abort the rest — an entry without buffer is simply skipped
      // during scheduling. Empty entries array is fine (no-op).
      const decodes: Promise<unknown>[] = entries.map((e) =>
        decodeAndCache(e.take.buffer.slice(0), e.take.id)
          .then((buf) => { e.buffer = buf; })
          .catch((err) => console.warn('[preview] decodeAudioData failed for take', e.take.id, err)),
      );
      // Decode every bed track alongside the voice takes. Both tracks (max
      // 2) decode in parallel; sfxBedRouting's per-track cache is keyed by
      // track index so re-decode-on-replay is rare.
      const session = useSessionStore.getState();
      const beds = session.sfxBeds[session.currentAssetIdx] ?? [];
      beds.forEach((bed, idx) => {
        if (!bed.buffer) return;
        const trackIdx = (idx === 0 ? 0 : 1) as 0 | 1;
        decodes.push(decodeBedBuffer(ac, bed.buffer, trackIdx));
      });
      if (decodes.length > 0) {
        await Promise.allSettled(decodes);
      }

      if (r.cancel) return; // stop() called during the decode await

      // Seek the <video> element (cheap, no playback) before scheduling
      // audio — then delay play() by the measured output-latency so video
      // frames emit at the same perceptual moment as the first audio sample.
      //
      // CRITICAL: we await `seekVideoTo` rather than just assigning
      // `v.currentTime`. HTMLMediaElement seeks are async, and the <video>
      // is NOT muted (see note below) — so calling `v.play()` before the
      // seek lands lets the element emit audio from the OLD playhead for a
      // few frames. That's the post-stop "audio snippet" bug: stop() leaves
      // currentTime at the stop position, and a subsequent play(0) without
      // awaiting the seek replays a tail of that old position before
      // snapping to 0.
      const v = getActiveVideo();
      const hasVideo = !!v && v.readyState >= 1 && Number.isFinite(v.duration);
      if (hasVideo && v) {
        await seekVideoTo(v, startFrom);
        // NOTE: we deliberately do NOT mute the <video> element here.
        // Forcing v.muted = true caused frame stutter in some browsers
        // (the audio decode appears to participate in the playback clock).
        // Trade-off: the video's native audio track plays in parallel with
        // the Web Audio voice/bed mix. Mixer-driven volume on the <video>
        // (via useVideoDucking) still applies, so users can attenuate it.
        // A cleaner fix would route the element through a
        // MediaElementAudioSourceNode, but that's a larger change.
      }

      // After-await re-check: stop() may have landed during seekVideoTo.
      if (r.cancel) return;

      scheduleFrom(startFrom);

      if (hasVideo && v) {
        const startVideo = () => {
          // Defensive: if stop() landed between schedule + this fire,
          // r.cancel will be set and we must NOT resume the <video>. Without
          // this guard the deferred play() restarts the (now paused) video,
          // producing the ~1s tail snippet the user hears after stopping.
          //
          // We only check r.cancel here — NOT !r.playing. The latency==0
          // branch below calls startVideo() synchronously while r.playing is
          // still false (it's set a few lines down at `r.playing = true`),
          // so a !r.playing guard would silently skip v.play() on every
          // small-latency click. r.cancel alone is the correct abort signal:
          // stop() sets it, and that's the only path that needs to inhibit
          // the deferred fire.
          if (r.cancel) return;
          v.play().catch(() => {
            /* autoplay blocked — ignore */
          });
        };
        // Read the live `previewOutputLatency` export (set inside
        // scheduleFrom) via the namespace import so we pick up the current
        // binding, not the value at module-load time.
        const latency = AudioCtx.previewOutputLatency;
        if (latency > 0.005) {
          // Track the handle so stop() can clear it before it fires.
          const h = window.setTimeout(startVideo, latency * 1000);
          r.pendingTimeouts.push(h);
        } else {
          startVideo();
        }
      }

      r.playing = true;
      setIsPlayingState(true);
      runRaf();
    },
    [blocks, totalDuration, bedAssetLen, scheduleFrom, runRaf],
  );

  // --- stop ---------------------------------------------------------------
  const stop = useCallback(() => {
    const r = ref.current;
    r.cancel = true;
    if (r.rafHandle !== null) {
      cancelAnimationFrame(r.rafHandle);
      r.rafHandle = null;
    }
    // Clear any deferred video.play() timeouts queued for the latency
    // alignment window. Without this, a user-initiated stop within ~0.1–
    // 0.35s of play() lets the timeout fire AFTER stop, restarting the
    // <video> element and producing a residual audio snippet.
    for (const h of r.pendingTimeouts) {
      try { window.clearTimeout(h); } catch { /* ignore */ }
    }
    r.pendingTimeouts = [];
    stopAllPreviewSources();
    stopBedSource();
    if (r.lastActiveBlock !== -1) {
      unduck();
      r.lastActiveBlock = -1;
    }
    r.playing = false;
    setIsPlayingState(false);
    const v = getActiveVideo();
    if (v && !v.paused) {
      try { v.pause(); } catch { /* ignore */ }
    }
    // Release the global playback mutex slot if it's still ours.
    clearPlayback('timeline');
  }, [unduck]);

  // --- seek ---------------------------------------------------------------
  const seek = useCallback(
    (sec: number) => {
      const r = ref.current;
      const clamped = Math.max(0, Math.min(totalDuration || sec, sec));
      if (r.playing) {
        // Fresh reschedule from the new position.
        stop();
        // void — don't need to await; the play() promise resolves async.
        void play(clamped);
      } else {
        setPlayheadSec(clamped);
        const v = getActiveVideo();
        if (v && Number.isFinite(v.duration)) {
          try { v.currentTime = Math.min(clamped, v.duration || 0); } catch { /* ignore */ }
        }
      }
    },
    [play, stop, totalDuration],
  );

  // Listen for stop-preview broadcasts from TTSView pipelines.
  useEffect(() => {
    const off = onStopPreview(() => { stop(); });
    return off;
  }, [stop]);

  // Register with the global playback mutex so a take chip click / pipeline
  // start can stop the Timeline preview without going through the legacy
  // pipelineCoordinator channels. This is the symmetric counterpart of
  // the take/pipeline registrations in TTSView.
  useEffect(() => registerPlaybackStop('timeline', stop), [stop]);

  // Unmount cleanup — stop the preview so the parent can tear down.
  useEffect(() => {
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(
    () => ({ play, stop, seek, isPlaying, playheadSec }),
    [play, stop, seek, isPlaying, playheadSec],
  );
}
